import {createGoogleMapOverlayClass} from "./GoogleMapOverlayFactory.js";

export class GoogleMapsAdapter {
    _googleApi = null;
    _mapInstance = null;
    _markers = null;

    _overlayClass = null;
    _overlayInstance = null;

    /**
     * @param {object} googleApiObject - das globale 'google' Objekt
     */
    constructor(googleApiObject) {
        if(!googleApiObject || !googleApiObject.maps) {
            throw new Error("Google Maps API object not provided or invalid.");
        }
        this._googleApi = googleApiObject;
        this._markers = new Map();
    }

    // Overlays
    setOverlayClass(googleApiObject) {
        this._overlayClass = createGoogleMapOverlayClass(googleApiObject);
    }

    getOverlayInstance() {
        if(!this._overlayInstance && this._overlayClass) {
            this._overlayInstance = new this._overlayClass(this.getMapInstance());
        }
        return this._overlayInstance;
    }

    removeOverlay() {
        if(this._overlayInstance) {
            this._overlayInstance.remove();
            this._overlayInstance = null;
        }
    }

    createWmtsImageMapType({
        baseUrl,
        version,
        layer,
        style = "default",
        tileMatrixSet,
        format = "png",
        minZoom = 0,
        maxZoom = 19,
        opacity = 1.0
    }) {
        const wmtsOverlay = new this._googleApi.maps.ImageMapType({
            getTileUrl: ({x, y}, z) => {
                const url = `${baseUrl}/${version}/${layer}/${style}/current/${tileMatrixSet}/` +
                    `${z}/${x}/${y}.${format}`;
                return url;
            },
            tileSize: new this._googleApi.maps.Size(256, 256),
            minZoom,
            maxZoom,
            opacity,
            name: "WMTS-Overlay"

        });
        wmtsOverlay._customName = "WMTS-Overlay";
        return wmtsOverlay;
    }

    /**
     * Initialisiert die Google Map Instanz
     * @param {string} domElementId - ID des Container-Elements
     * @param {object} options - Kartenoptionen
     */
    initializeMap(domElementId, options) {
        const element = document.getElementById(domElementId);
        if (!element) {
            console.error(`Map container with ID "${domElementId}"not found.`);
            return;
        }

        const gmapOptions = {
            center: options.center || {lat: 0, lng: 0},
            zoom: options.zoom || 8,
            mapTypeId: options.mapTypeId || this._googleApi.maps.MapTypeId.ROADMAP,
            // hardcoded optionen aus frontendMap.js
            gestureHandling: options.gestureHandling,
            zoomControl: false,
            mapTypeControl: true,
            streetViewControl: false,
            panControl: true,
            rotateControl: true,
            fullscreenControl: false,
            disableDefaultUI: true,
            mapId: "fe5b3fec33f8c43e"
        };

        try {
            this._mapInstance = new this._googleApi.maps.Map(element, gmapOptions);
            console.debug("[GoogleMapsAdapter] Map instance created via Adapter: ", this._mapInstance);
        } catch(e) {
            console.error('Error creating map instance in Adapter: ', e);
            this._mapInstance = null; // Sicherstellen, dass Instanz null ist bei Fehler
        }

        //this._mapInstance.mapTypes.set('dufour', dufourMapType);
        //this._mapInstance.setMapTypeId('dufour');
    }

    getWMTSImageMapType(overlayType) {
        switch(overlayType) {
            case 'dufour':
                return this.createWmtsImageMapType(            {
                    baseUrl: 'https://wmts.geo.admin.ch',
                    version: '1.0.0',
                    layer: 'ch.swisstopo.hiks-dufour',
                    tileMatrixSet: '3857',
                    format: 'png',
                    opacity: 1
                });
            default:
                return null;
        }
    }

    setWMTSMapOverlay(){
        const WMTSMapType = this.getWMTSImageMapType('dufour');
        if(WMTSMapType) {
            const container = document.createElement('div');
            container.className = 'wmts-visibility-controls';
            container.style.padding = '10px';
            container.style.margin = '10px';
            container.style.backgroundColor = 'white';
            container.style.borderRadius = '2px';

            const title = document.createElement('div');
            title.innerHTML = '<b>Kartenoverlays</b>';
            title.style.marginBottom = '3px';
            container.appendChild(title);

            const overlayCheckbox = document.createElement('input');
            overlayCheckbox.type = 'checkbox';
            overlayCheckbox.id = 'dufourCheckbox';
            overlayCheckbox.value = 'Dufour';
            overlayCheckbox.style.margin = '0';
            overlayCheckbox.style.marginRight = '5px';
            const overlayCheckboxLabel = document.createElement('label');
            overlayCheckboxLabel.innerHTML = 'Dufour';
            overlayCheckboxLabel.setAttribute('for', 'dufourCheckbox');
            container.appendChild(overlayCheckbox);
            container.appendChild(overlayCheckboxLabel);

            overlayCheckbox.addEventListener('click', () => {
                if(overlayCheckbox.checked) {
                    this._mapInstance.overlayMapTypes.insertAt(0, WMTSMapType);
                } else {
                    this._mapInstance.overlayMapTypes.removeAt(0);
                }
            });

            this.performMapAction('addControl', {
                element: container,
                position: 'TOP_RIGHT',
                type: 'WMTSOverlay'
            });
        } else {
            console.warn('[GoogleMapsAdapter] Can\'t find WMTS-MapTypeName');
        }
    }

    /**
     * Gibt die interne Google Maps Instanz zurück (für Übergangszwecke)
     * @returns {google.maps.Map | null}
     */
    getMapInstance() {
        return this._mapInstance;
    }

    /** Listener **/

    addListener(mapInstance, eventName, callback) {
        return this._googleApi.maps.event.addListener(mapInstance, eventName, callback);
    }

    removeListener(listener) {
        this._googleApi.maps.event.removeListener(listener);
    }

    clearListeners(target, eventName) {
        // ToDo: calls ohne eventName behandeln
        if(!eventName) {
            console.warn('[Adapter] clearListeners called without eventName!.');
            //return;
        }
        this._googleApi.maps.event.clearListeners(target, eventName);
    }

    trigger(mapInstance, eventName) {
        this._googleApi.maps.event.trigger(mapInstance, eventName);
    }

    /** ui controls **/
    createControls(domElement, position = 'RIGHT_BOTTOM', type = null){
        if(!this._mapInstance || !domElement) { return; }

        const ctrlPos = this._googleApi.maps.ControlPosition[position];
        if(ctrlPos === undefined) {
            console.warn(`Unknown control position: ${position}`);
            return;
        }
        console.debug(`[Adapter] Adding control ${type} at position ${position} `);
        this._mapInstance.controls[ctrlPos].push(domElement);
    }

    /** graphics elements (circle, polygon, etc) **/

    createCircle(options) {
        return new this._googleApi.maps.Circle(options);
    }

    createPolygon(options) {
        return new this._googleApi.maps.Polygon(options);
    }

    /** position utilities **/
    createLatLng(lat, lng) {
        return new this._googleApi.maps.LatLng(lat, lng);
    }

    createLatLngBounds() {
        return new this._googleApi.maps.LatLngBounds();
    }

    getDistance(point1, point2) {
        return this._googleApi.maps.geometry.spherical.computeDistanceBetween(point1, point2);
    }

    /** map actions - i.e. zoom, pan, fitTo (maybe?) **/
    performMapAction(action, payload) {
        switch (action) {
            case 'setZoom':
                if (this._mapInstance) {
                    if(payload.level !== undefined) {
                        this._mapInstance.setZoom(payload.level);
                    } else if(payload.difference !== undefined) {
                        this._mapInstance.setZoom(this._mapInstance.getZoom() + payload.difference);
                    }
                }
                break;

            case 'panTo':
                if (this._mapInstance) {
                    this._mapInstance.panTo(this.createLatLng(payload.lat, payload.lng));
                }
                break;

            case 'addControl':
                this.createControls(payload.element, payload.position || 'RIGHT_BOTTOM', payload.type);
                break;

            case 'getMapTypeConstant':
                if(!payload?.type) { return; }
                switch(payload.type) {
                    case 'satellite':   return this._googleApi.maps.MapTypeId.SATELLITE;
                    case 'roadmap':     return this._googleApi.maps.MapTypeId.ROADMAP;
                    case 'terrain':     return this._googleApi.maps.MapTypeId.TERRAIN;
                    case 'hybrid':      return this._googleApi.maps.MapTypeId.HYBRID;
                    default:
                        console.warn('Uknown mapTypeId: ', payload.type);
                        return null;
                }
            default:
                console.warn(`Unknown map action: ${action}`);
        }
    }

    fitMapToMarkers(map, markers, pan = false, zoomIdle = true) {
        if(!map || !markers || !markers.length) { return; }

        const bounds = new google.maps.LatLngBounds();

        for(const marker of markers) {
            if(marker.getBounds && typeof marker.getBounds === 'function') {
                bounds.union(marker.getBounds());
            } else if(marker.position) {
                bounds.extend((typeof marker.position === 'function' ? marker.position() : marker.position ));
            }
        }

        // Optionale Erweiterung für Marker mit Circle
        if(markers.circle && typeof markers.circle.getBounds === 'function') {
            bounds.union(markers.circle.getBounds());
        }

        // ToDo: schauen ob pan überhaupt noch benutzt wird
        if(pan) {
            map.panToBounds(bounds);
        } else {
            map.fitBounds(bounds);
        }

        if(zoomIdle) {
            google.maps.event.addListenerOnce(map, 'idle', () => {
                if(map.getZoom() > 17) { map.setZoom(17); }
            });
        }
    }
}